import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Slider from "react-slick";
import { useSwipeable } from "react-swipeable";

import Link from "../../../link/link";
import ErrorBoundary from "../../../../error-boundary";
import MorphSlider from "./morph-slider";
import { useSelector } from "react-redux";
import LazyLoad from "react-lazyload";
import Image from "@default/general-components/image/image";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import UniversalSliderButton from "@default/general-components/universal-slider-button";

const objectToArray = (myObject) => {
  let imageArray = [];
  Object.keys(myObject).forEach((item) => {
    const url = myObject[item].fieldImage?.fieldMediaImage?.style?.url;
    if (url) imageArray.push(url);
  });
  return imageArray;
};

const restructure = (urls) => {
  let array = [...urls];
  let first = array[0];
  array.shift();
  array.push(first);
  return array;
};

const ParagraphTeaserSlider = ({ content }) => {
  const slider = useRef();
  const sliderRef = useRef();
  const isMobile = useSelector((state) => state.appStore.isMobile);

  const isAnimating = () => slider.current?.classList.contains("changing");

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => { if (!isAnimating()) sliderRef.current?.slickNext(); },
    delta: 50,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
    trackMouse: true,
  });
  const { simpleDesign } = useAccessibilityContext();

  const [morphSliderContent, setMorphSliderContent] = useState([]);
  const [slides, setSlides] = useState(content.fieldTeaserSlides);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (slides) {
      if (slides.length <= 2) {
        setSlides([...slides, ...slides]);
      }
      setMorphSliderContent(objectToArray(slides));
    }
  }, [content.fieldTeaserSlides, currentSlide]);

  const sliderSettings = {
    arrows: !!simpleDesign,
    dots: false,
    slidesToShow: 2,
    slidesToScroll: 1,
    centerMode: false,
    infinite: true,
    touchMove: true,
    beforeChange: (oldIndex, newIndex) => {
      slider.current.classList.add("changing");
      setCurrentSlide(newIndex);
      document.getElementById(`slider-${content.id}-1`)?.__sketch?.next();
      document.getElementById(`slider-${content.id}-2`)?.__sketch?.next();
      setTimeout(() => {
        slider.current.classList.remove("changing");
      }, 2000);
    },
    responsive: [
      {
        breakpoint: 786,
        settings: {
          slidesToShow: 1,
          touchMove: true,
        },
      },
      {
        breakpoint: 550,
        settings: {
          slidesToShow: 1,
          touchMove: true,
        },
      },
    ],
  };

  const sectionClassNames = classNames({
    "paragraph paragraph-teaser-slider": true,
  });

  return (
    <section
      className={sectionClassNames}
      data-darkmode={content.fieldDarkMode}
    >
      <ErrorBoundary>
        {slides && (
          <>
            {!simpleDesign && (
              <React.Suspense>
                <LazyLoad offset={500}>
                  <div className="slider-wrapper" {...swipeHandlers}>
                    <MorphSlider
                      content={morphSliderContent}
                      id={`slider-${content.id}-1`}
                      isMobile={isMobile}
                    />
                    <MorphSlider
                      content={restructure(morphSliderContent)}
                      id={`slider-${content.id}-2`}
                      isMobile={isMobile}
                    />
                    <UniversalSliderButton
                      type="next"
                      onClick={() => sliderRef.current?.slickNext()}
                    />
                  </div>
                </LazyLoad>
              </React.Suspense>
            )}

            <div
              className={`content-slider-wrapper ${
                simpleDesign && "container no-padding"
              }`}
              ref={slider}
              role="region"
              aria-roledescription="carousel"
              aria-label={content.fieldHeading || "Teaser-Slider"}
            >
              <Slider {...sliderSettings} ref={sliderRef}>
                {slides.map((item, index) => {
                  const isCurrent = currentSlide === index;
                  const headingId = `slide-${index}-heading`;

                  return (
                    <React.Fragment key={index}>
                      <article
                        className="col-12 position-static"
                        role="group"
                        aria-roledescription="slide"
                        aria-labelledby={item.fieldHeading}
                        aria-current={isCurrent ? "true" : undefined}
                      >
                        {item.fieldImage && simpleDesign && (
                          <Image data={item.fieldImage?.fieldMediaImage} />
                        )}

                        {item.fieldHeading && (
                          <h2 id={headingId}>{item.fieldHeading}</h2>
                        )}

                        {item.fieldText && (
                          <div
                            className="text body-m"
                            dangerouslySetInnerHTML={{ __html: item.fieldText }}
                            aria-live={isCurrent ? "polite" : undefined}
                          />
                        )}

                        {item.fieldLink && (
                          <Link
                            className="link body-m"
                            link={item.fieldLink}
                            tabIndex={isCurrent ? "0" : "-1"}
                            hoverAnimation={true}
                          />
                        )}
                      </article>
                    </React.Fragment>
                  );
                })}
              </Slider>
            </div>
          </>
        )}
      </ErrorBoundary>
    </section>
  );
};

ParagraphTeaserSlider.propTypes = {
  viewMode: PropTypes.string,
  content: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fieldHeading: PropTypes.string,
    fieldTeaserSlides: PropTypes.arrayOf(
      PropTypes.shape({
        fieldHeading: PropTypes.string,
        fieldText: PropTypes.string,
        fieldImage: PropTypes.shape({
          fieldMediaImage: PropTypes.shape({
            alt: PropTypes.string,
            title: PropTypes.string,
            style: PropTypes.shape({
              url: PropTypes.string.isRequired,
            }),
          }),
        }),
        fieldLink: PropTypes.shape({
          uri: PropTypes.object,
        }),
      })
    ),
  }),
};

export default ParagraphTeaserSlider;
